import { createEntityAdapter, EntityState } from '@reduxjs/toolkit';
import { on, reducer } from 'ts-action';
import { Cluster } from '../clustering.types';
import * as clusterActions from '../actions/cluster.actions';

export interface ClusterState extends EntityState<Cluster> {
  selectedIds: Set<number>;
}

declare module 'react-redux' {
  interface DefaultRootState {
    cluster: ClusterState;
  }
}

export const sortComparer = (a: Cluster, b: Cluster) => {
  return a.priority - b.priority;
};

const clusterAdapter = createEntityAdapter<Cluster>({
  sortComparer,
});
export const { selectAll, selectEntities, selectById } =
  clusterAdapter.getSelectors();

export const getInitialClusterState = (): ClusterState =>
  clusterAdapter.getInitialState({
    selectedIds: new Set(),
    selectedBeatIds: new Set(),
  });

export default reducer<ClusterState>(
  getInitialClusterState(),
  on(clusterActions.cleanUp, () => getInitialClusterState()),
  on(clusterActions.load, (state, { clusters }): ClusterState => {
    const updatedState = {
      ...state,
      selectedIds: new Set(clusters.length !== 0 ? [clusters[0].id] : []),
    };
    return clusterAdapter.setAll(updatedState, clusters);
  }),
  on(clusterActions.selectClusters, (state, { clusterIds }) => ({
    ...state,
    selectedIds: clusterIds,
  })),
  on(clusterActions.replaceClusters, (state, { newClusters, oldClusters }) => {
    /**
     * On one old cluster and several new clusters:
     * We replace the old cluster with an arbitrary array of clusters.
     * We need to shift the priorities as many times as the number of new clusters we are inserting
     * because the new clusters must start from the priority of the old cluster
     */
    let newState = state;
    if (oldClusters.length === 1 && newClusters.length > 1) {
      const clusters = clusterAdapter.getSelectors().selectAll(state);
      const prioritizedClusters = clusters.map((cluster) => {
        if (cluster.priority > oldClusters[0].priority) {
          return {
            ...cluster,
            priority: cluster.priority + newClusters.length - 1,
          };
        }
        return cluster;
      });
      // Set the clusters that follow with their new priorities
      newState = clusterAdapter.setAll(newState, prioritizedClusters);
    }
    // Add the new clusters
    newState = clusterAdapter.addMany(newState, newClusters);
    // The new selected cluster is the first one of the new clusters
    newState = { ...newState, selectedIds: new Set([newClusters[0].id]) };
    // Remove the old cluster
    const oldClusterIds = oldClusters.map((oldCluster) => oldCluster.id);
    return clusterAdapter.removeMany(newState, oldClusterIds);
  }),
  on(clusterActions.removeClusters, (state, { clusterIds }) =>
    clusterAdapter.removeMany(state, clusterIds),
  ),
  on(clusterActions.addClusters, (state, { clusters }) => {
    // Re-prioritize by nb of beats (desc)
    const sortedClusters = [
      ...clusterAdapter.getSelectors().selectAll(state),
      ...clusters,
    ]
      .sort((a, b) => b.beats.length - a.beats.length)
      .map((cluster, i) => ({
        ...cluster,
        priority: i,
      }));

    return clusterAdapter.setAll(state, sortedClusters);
  }),
);
